import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, Link } from "wouter";

import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Alert from "react-bootstrap/Alert";

import { absURL } from '../utils';
import QuizApi from '../QuizApi';
import FileUploadMultiple from './FileUploadMultiple';
import AdminHeader from './AdminHeader';
import InfoTooltip from './InfoTooltip';
import DeezerSearch from './DeezerSearch';

export default function AdminQuizQuestionEdit(props) {

  const { quizId, quizQuestionId } = props;

  const [, setLocation] = useLocation();
  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState(1);
  const [questionMaxPoints, setQuestionMaxPoints] = useState(1);
  const [questionSingleAnswer, setQuestionSingleAnswer] = useState(false);
  const [feedbackMarkdown, setFeedbackMarkdown] = useState("");
  const [inputMode, setInputMode] = useState(false);
  const [acceptedAnswers, setAcceptedAnswers] = useState([""]);
  const [existingMediaList, setExistingMediaList] = useState([]);
  const [quizType, setQuizType] = useState("");
  const [selectedTrack, setSelectedTrack] = useState(null);

  const [answerList, setAnswerList] = useState([
    { text: "", correct: false },
    { text: "", correct: false }
  ]);

  const [fileList, setFileList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayError, setDisplayError] = useState(false);
  const [submitErrorMsg, setSubmitErrorMsg] = useState(false);

  // ===========================
  // Effect to fetch question 
  // ===========================
  useEffect(() => {

    const fn = async () => {

      const result = await QuizApi.quizQuestionGet(quizId, quizQuestionId);

      if (result && result.success && result.response && result.response.question) {

        const question = result.response.question;

        setQuestionText(question.markdown);
        setQuestionType(question.typeId);
        setQuestionMaxPoints(question.maxPoints);
        setQuestionSingleAnswer(question.config?.single_answer);
        setFeedbackMarkdown(question.feedbackMarkdown || "");
        setInputMode(!!question.config?.inputMode);
        setAcceptedAnswers(
          (question.config?.acceptedAnswers || []).length > 0
            ? question.config.acceptedAnswers
            : [""]
        );
        
        if (question.answerList)
        {
          setAnswerList(question.answerList.map(a => ({
            id: a.id,
            text: a.text,
            correct: a.correct
          })))
        }

        if (question.mediaList)
        {
          setExistingMediaList(question.mediaList);
        }

        if (question.externalMediaList && question.externalMediaList.length > 0) {
          const em = question.externalMediaList[0];
          setSelectedTrack({
            id:         em.metadata.trackId,
            previewUrl: em.previewUrl,
            title:      em.metadata.title,
            artist:     em.metadata.artist,
            album:      em.metadata.album,
            cover:      em.metadata.cover,
            deezerUrl:  em.metadata.deezerUrl,
          });
        }

        if (result.response.quiz)
        {
          setQuizType(result.response.quiz.type);
        }
      }

      setLoading(false);

    };

    fn();

  }, [quizId, quizQuestionId]);

  function removeExistingMedia(id) {
    setExistingMediaList(existingMediaList.filter(m => m.id !== id));
  };

  function setAnswerText(index, text) {
    setAnswerList([
      ...answerList.slice(0, index), 
      {...answerList[index], text }, 
      ...answerList.slice(index + 1)]
    );
  }

  function setAnswerCorrect(index, correct) {
    setAnswerList([
      ...answerList.slice(0, index), 
      {...answerList[index], correct }, 
      ...answerList.slice(index + 1)]
    );
  }

  function removeAnswer(index) {
    setAnswerList([...answerList].filter((a, idx) => idx !== index));
  }

  // =======================
  // Form submit callback
  // =======================
  const onSubmit = useCallback(ev => {

    ev.preventDefault();

    if (!(questionText || "").trim()) {
      setDisplayError(true);
      return;
    }

    setLoading(true);

    const trimmedAcceptedAnswers = acceptedAnswers
      .map(a => (a || "").trim())
      .filter(a => a.length > 0);

    const data = new FormData();
    data.append("questionText", questionText);
    data.append("questionType", questionType);
    data.append("questionMaxPoints", questionMaxPoints);
    data.append("questionSingleAnswer", questionSingleAnswer);
    data.append("feedbackMarkdown", feedbackMarkdown);
    data.append("inputMode", inputMode);
    data.append("acceptedAnswersJSON", JSON.stringify(trimmedAcceptedAnswers));
    data.append("existingMediaIdsToKeepJSON", JSON.stringify(existingMediaList.map(m => m.id)));
    data.append("answerListJSON", JSON.stringify(inputMode ? [] : answerList));

    if (quizType === "Musicquiz") {
      data.append("externalMediaJSON", JSON.stringify(selectedTrack ? [{
        source:     "deezer",
        trackId:    selectedTrack.id,
        title:      selectedTrack.title,
        artist:     selectedTrack.artist,
        album:      selectedTrack.album,
        cover:      selectedTrack.cover,
        previewUrl: selectedTrack.previewUrl,
        deezerUrl:  selectedTrack.deezerUrl,
        mimeType:   "audio/mpeg"
      }] : []));
    }

    if (fileList) {
      for (const file of fileList) {
        data.append("questionMedia", file, file.name);
      }
    }

    const fn = async () => {

      let result = null;

      try {
        const httpResponse = await fetch(`/api/quiz/${quizId}/question/${quizQuestionId}/edit`, {
          method: 'POST',
          body: data
        });

        result = await httpResponse.json();
      } catch(e) {
        setDisplayError(true);
        setSubmitErrorMsg(e.message);
      }

      setLoading(false);

      if (result && result.success) {
        setDisplayError(false);
        setSubmitErrorMsg("");
        setQuestionText("");
        setQuestionType(1);
        setQuestionMaxPoints(1);
        setFileList([]);
        setLocation(absURL(`/admin/quiz/${quizId}/questions`));
      }
      else {
        setDisplayError(true);
        setSubmitErrorMsg("Det uppstod ett fel när vi försökte skapa frågan.");
      }

    };

    fn();

  }, [ quizId, quizQuestionId, questionText, questionType, questionMaxPoints, questionSingleAnswer, feedbackMarkdown, inputMode, acceptedAnswers, answerList, fileList, existingMediaList, selectedTrack, quizType, setLocation])

  return (
		<div id="quizQuestionEdit">
			<div>

        {loading && (
          <div className="d-flex justify-content-center align-items-center h-100">
            <Spinner animation="border" />
          </div>
        )}

        {!loading && 
          <div>
            <AdminHeader />
            <div className="adminContentWrapper">
              <form onSubmit={onSubmit} >

                <Button variant="outline-secondary" size="sm" onClick={() => window.history.back()}>
                  Tillbaka
                </Button>

                <div className="form-group">
                  <label for="questionMarkdown">Frågetext:</label>
                  <textarea
                    class="form-control"
                    cols="40" rows="5"
                    name="questionMarkdown"
                    value={questionText}
                    onChange={ev => setQuestionText(ev.target.value)}>
                  </textarea>
                </div>

                {displayError && !(questionText || "").trim() && <Alert variant={"danger"}>
                  Du måste ange en frågetext
                </Alert>
                }

                {quizType === "Musicquiz" && (
                  <DeezerSearch
                    selectedTrack={selectedTrack}
                    onTrackSelected={track => setSelectedTrack(track)}
                  />
                )}

                <div className="form-group">
                  <label for="feedbackMarkdown">
                    Ev. Feedback{" "}
                    <span class="font-italic">
                      (visas för deltagare efter svar — framtida funktion):
                    </span>
                  </label>
                  <textarea
                    class="form-control"
                    cols="40" rows="3"
                    name="feedbackMarkdown"
                    value={feedbackMarkdown}
                    onChange={ev => setFeedbackMarkdown(ev.target.value)}>
                  </textarea>
                </div>

                {existingMediaList.length > 0 &&
                  <div className="form-group">
                    <label>
                      Uppladdade filer
                    </label>
                    <ul class="list-group" style={{color: "black"}}>

                      {existingMediaList.map((m, idx) => 
                        <li class="list-group-item">
                          <span>{m.name}</span>
                          <span 
                            onClick={() => removeExistingMedia(m.id)} 
                            class="btn btn-sm btn-quizSecondaryDark float-right">
                            Ta bort
                          </span>
                        </li>
                      )}

                    </ul>
                  </div>
                }

                <div className="form-group">
                  <label>
                    Ladda upp filer <span class="font-italic">(endast bilder kommer att visas i quizläget):</span>
                  </label>
                  <FileUploadMultiple onFilesChanged={newFileList => setFileList(newFileList)}/>
                </div>

                {quizType === "Quiz" && 
                  <>
                  <div className="form-group">
                    <label for="questionType">Poängtyp:</label>
                    <select name="questionType" class="form-control" onChange={ev => setQuestionType(ev.target.value)}>
                      <option selected={Number(questionType) === 1} value="1">Allt eller inget</option>
                      <option selected={Number(questionType) === 2} value="2">Procentandel</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label for="questionMaxPoints">Maxpoäng:</label> 
                    <input 
                      type="number" 
                      name="questionMaxPoints" 
                      class="form-control" 
                      value={questionMaxPoints} min="1" 
                      onChange={ev => setQuestionMaxPoints(ev.target.value)} /> 
                  </div>
                  </>
                }

                {quizType === "Quiz" &&
                  <div className="form-check mt-2">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      id="inputMode"
                      checked={inputMode}
                      onChange={ev => setInputMode(ev.target.checked)}
                    />
                    <label class="form-check-label" for="inputMode">
                      Inputläge (svar via textfält)
                      <InfoTooltip placement="left" className="ml-1">
                        <p>Deltagaren skriver sitt svar i ett textfält istället för att välja bland alternativ. Svaret jämförs (utan skiftläge och kringliggande mellanslag) mot de accepterade svaren.</p>
                      </InfoTooltip>
                    </label>
                  </div>
                }

                {quizType === "Quiz" && inputMode &&
                  <div className="form-group mt-3">
                    <label>Accepterade svar:</label>
                    <div>
                      {acceptedAnswers.map((value, idx) =>
                        <div className="form-inline" key={idx}>
                          <input
                            type="text"
                            value={value}
                            onChange={ev => setAcceptedAnswers([
                              ...acceptedAnswers.slice(0, idx),
                              ev.target.value,
                              ...acceptedAnswers.slice(idx + 1)
                            ])}
                            className="form-control mt-2 mr-2"
                            placeholder="Ett accepterat svar (t.ex. '2' eller 'två')" />
                          {acceptedAnswers.length > 1 &&
                            <span
                              onClick={() => setAcceptedAnswers(acceptedAnswers.filter((_, i) => i !== idx))}
                              class="btn btn-sm btn-quizSecondaryDark ml-1">
                              Ta bort
                            </span>
                          }
                        </div>
                      )}
                    </div>
                    <Button
                      type="button"
                      className="btn-sm mt-2"
                      onClick={() => setAcceptedAnswers([...acceptedAnswers, ""])}>
                      Lägg till accepterat svar
                    </Button>
                  </div>
                }

                {quizType !== "Free" && !inputMode &&
                <div>

                  <div>

                    <input
                      type="checkbox"
                      className="mr-1"
                      id="questionSingleAnswer"
                      checked={questionSingleAnswer}
                      onChange={ev => setQuestionSingleAnswer(ev.target.checked)}
                    />

                    <label class="form-check-label" for="questionSingleAnswer">
                      Deltagare får endast ange ett svar
                    </label>

                  </div>

                  <label for="questionMaxPoints" className="mt-3">
                    Svarsalternativ:
                  </label> 

                  <div>
                    {answerList.map((a, idx) => 
                      <div className="form-inline">
                        <div class="form-group mr-1">
                          <input 
                            type="text" 
                            key={idx} 
                            value={answerList[idx].text}
                            onChange={ev => setAnswerText(idx, ev.target.value)}
                            className="form-control mt-2 mr-2" 
                            placeholder="Skriv en svarstext" />
                        </div>
                        <div class="form-group">
                          {quizType === "Quiz" && 
                            <>
                              <input 
                                class="form-check-input" 
                                type="checkbox" 
                                id={`questionAnswerCorrect_${idx}`} 
                                checked={!!answerList[idx].correct}
                                onChange={ev => setAnswerCorrect(idx, ev.target.checked)}
                                />
                              <label class="form-check-label mr-2" for={`questionAnswerCorrect_${idx}`}>
                                Korrekt alternativ?
                              </label>
                            </>
                          }
                          <span 
                            onClick={() => removeAnswer(idx)}
                            class="btn btn-sm btn-quizSecondaryDark ml-1">
                            Ta bort
                          </span>
                        </div>
                      </div>
                    )}
                  </div>

                  <Button 
                    type="button" 
                    className="btn-sm mt-2" 
                    onClick={() => setAnswerList([...answerList, { correct: false, text: "" }])}>
                    Nytt svarsalternativ
                  </Button>

                </div>
                }

                <Button type="submit" className="btn mt-4 btn-quizPrimaryDark">Spara</Button>

                {displayError && submitErrorMsg && 
                  <Alert variant={"danger"} className="mt-4">
                    {submitErrorMsg}
                  </Alert>
                }

              </form>

            </div>
          </div>
        }
      </div>
    </div>
  )
};